import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, Star } from "lucide-react";
import axios from "axios";
import { useSettings } from "../context/SettingsContext";
import WinningTicketBanner from "../components/WinningTicketBanner";
import { LatestPosts } from "../components/LatestPosts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/slsa_logo_transparent.png";

const MARQUEE_ITEMS = [
    "SKATE", "RIDE", "SEELEY LAKE", "COMMUNITY", "BMX", "SCOOTER",
    "ROLLERBLADES", "MONTANA", "BUILD IT", "FREE FOR ALL", "PUMP TRACK",
    "SWAN MOUNTAINS", "PYRAMID PEAK", "DOUBLE ARROW", "SEELEY BOWL",
];

const PARK_FEATURES = [
    { name: "Swan Mountains", type: "Transition Section" },
    { name: "Pyramid Peak", type: "Rail Feature" },
    { name: "Double Arrow", type: "Quarterpipe" },
    { name: "Seeley Bowl", type: "Concrete Bowl" },
];

const BtnPrimary = ({ to, children, testId, onClick }) => {
    const cls = "inline-block px-8 py-4 bg-[#C4522A] text-white font-anton uppercase tracking-wider text-base border-2 border-[#C9A84C] shadow-[5px_5px_0px_0px_rgba(201,168,76,0.7)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(201,168,76,0.7)] transition-transform duration-150 cursor-pointer";
    if (to) return <Link to={to} data-testid={testId} className={cls}>{children}</Link>;
    return <button onClick={onClick} data-testid={testId} className={cls}>{children}</button>;
};

const BtnOutline = ({ to, children, testId, light }) => (
    <Link to={to} data-testid={testId}
        className={`inline-block px-8 py-4 bg-transparent font-anton uppercase tracking-wider text-base border-2 transition-colors duration-200 ${
            light
                ? "text-[#F5EDD6] border-[#F5EDD6] hover:bg-[#F5EDD6] hover:text-[#3D2008]"
                : "text-[#3D2008] border-[#3D2008] hover:bg-[#3D2008] hover:text-[#F5EDD6]"
        }`}>
        {children}
    </Link>
);

const Home = () => {
    const [stats, setStats] = useState({ total_raised: 0, donor_count: 0, goal: 400000, percentage: 0 });
    const [events, setEvents] = useState([]);
    const { settings } = useSettings();
    const storeOn = !!settings?.store_enabled;

    useEffect(() => {
        axios.get(`${API}/donate/stats`).then(r => setStats(r.data)).catch(() => {});
        axios.get(`${API}/events`).then(r => setEvents(r.data.slice(0, 3))).catch(() => {});
    }, []);

    return (
        <div className="pt-16">
            <WinningTicketBanner />
            {/* ===== HERO ===== */}
            <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: "url('https://images.pexels.com/photos/5738357/pexels-photo-5738357.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920')" }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#3D2008]/85 via-[#3D2008]/70 to-[#3D2008]/90" />

                <div className="relative z-10 text-center px-4 max-w-5xl mx-auto py-20">
                    <img
                        src={LOGO_URL}
                        alt="SLSA Logo"
                        className="w-48 sm:w-64 h-auto mx-auto mb-8 object-contain drop-shadow-[0_4px_24px_rgba(201,168,76,0.4)]"
                    />
                    <h1 className="font-anton text-5xl sm:text-7xl lg:text-9xl text-[#F5EDD6] uppercase leading-none mb-3 tracking-wide">
                        Seeley Lake
                    </h1>
                    <h2 className="font-anton text-4xl sm:text-6xl lg:text-7xl text-[#C4522A] uppercase leading-none mb-6 tracking-wide">
                        Skatepark
                    </h2>
                    <p className="font-marker text-[#C9A84C] text-2xl sm:text-3xl mb-8 -rotate-1">
                        Build it. Ride it. Own it.
                    </p>
                    <p className="font-manrope text-[#F5EDD6]/80 text-lg sm:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
                        Help us build a free, modern concrete skatepark for every rider in Seeley Lake, Montana — skateboards, scooters, BMX, rollerblades, and beyond.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <BtnPrimary to="/donate" testId="hero-donate-btn">Donate Now</BtnPrimary>
                        <BtnOutline to="/get-involved" testId="hero-getinvolved-btn" light>Get Involved</BtnOutline>
                    </div>
                </div>

                {/* Scroll indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                    <div className="w-6 h-10 border-2 border-[#F5EDD6]/50 rounded-full flex items-start justify-center pt-2">
                        <div className="w-1.5 h-3 bg-[#F5EDD6]/50 rounded-full" />
                    </div>
                </div>
            </section>

            {/* ===== MARQUEE removed per Matt's notes ===== */}

            {/* ===== ABOUT TEASER ===== */}
            <section className="py-20 px-4" data-testid="about-teaser-section">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <div>
                        <span className="font-marker text-[#C4522A] text-2xl -rotate-2 inline-block mb-6">Who We Are</span>
                        <h2 className="font-anton text-4xl sm:text-5xl text-[#3D2008] uppercase leading-tight mb-6">
                            Grassroots.<br />Homegrown.<br /><span className="text-[#C4522A]">Determined.</span>
                        </h2>
                        <p className="font-manrope text-[#5C4A35] text-lg leading-relaxed mb-5">
                            The Seeley Lake Skatepark Association is a community-driven group of riders, parents, and neighbors on a mission to build something lasting — a free, modern concrete skatepark that welcomes everyone on wheels.
                        </p>
                        <p className="font-manrope text-[#5C4A35] text-lg leading-relaxed mb-8">
                            We're working side-by-side with local families, businesses, schools, and community leaders. This is YOUR park. Built by YOU. For YOU.
                        </p>
                        <Link to="/about" data-testid="about-learn-more-btn"
                            className="inline-flex items-center gap-2 py-2 font-anton uppercase tracking-wider text-[#C4522A] border-b-2 border-[#C4522A] hover:gap-4 transition-all duration-200">
                            Our Full Story <ArrowRight size={18} />
                        </Link>
                    </div>
                    <div className="relative">
                        <img
                            src="https://images.pexels.com/photos/4734781/pexels-photo-4734781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                            alt="Young skaters together"
                            className="w-full border-4 border-[#3D2008] shadow-[12px_12px_0px_0px_rgba(196,82,42,0.35)] rotate-1 object-cover"
                        />
                        <div className="absolute -bottom-4 -left-4 bg-[#C4522A] border-2 border-[#3D2008] px-4 py-2 shadow-[3px_3px_0px_0px_#3D2008] -rotate-2">
                            <span className="font-marker text-white text-lg">Free for Everyone!</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== FUNDRAISING ===== */}
            <section className="py-20 px-4 bg-[#3D2008]" data-testid="fundraising-section">
                <div className="max-w-4xl mx-auto text-center">
                    <span className="font-marker text-[#C9A84C] text-2xl -rotate-1 inline-block mb-4">Help Us Get There</span>
                    <h2 className="font-anton text-4xl sm:text-6xl text-[#F5EDD6] uppercase mb-4">The Build Fund</h2>
                    <p className="font-manrope text-[#F5EDD6]/60 text-lg mb-12">
                        Every dollar gets us closer to breaking ground in Seeley Lake.
                    </p>

                    <div className="mb-10">
                        <div className="flex justify-between items-end mb-3">
                            <div className="text-left">
                                <span data-testid="total-raised-amount" className="font-anton text-3xl sm:text-4xl text-[#C4522A]">
                                    ${stats.total_raised.toLocaleString()}
                                </span>
                                <span className="font-manrope text-[#F5EDD6]/60 text-sm ml-2">raised</span>
                            </div>
                            <div className="text-right">
                                <span className="font-anton text-xl text-[#F5EDD6]">
                                    ${stats.goal.toLocaleString()}
                                </span>
                                <span className="font-manrope text-[#F5EDD6]/60 text-sm ml-1">goal</span>
                            </div>
                        </div>
                        {/* Grind rail progress bar */}
                        <div className="relative h-8 bg-[#F5EDD6]/10 border-2 border-[#C9A84C] overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-[#C4522A] to-[#E06235] transition-all duration-1000 ease-out relative"
                                style={{ width: `${Math.max(stats.percentage, 0.5)}%` }}
                            >
                                <div className="absolute right-0 top-0 h-full w-2 bg-[#C9A84C]" />
                                <div className="absolute inset-0 flex items-center gap-4 pl-3 overflow-hidden opacity-40">
                                    {Array.from({ length: 30 }).map((_, i) => (
                                        <div key={i} className="w-px h-4 bg-white flex-shrink-0" />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-between mt-3">
                            <span data-testid="fundraising-percentage" className="font-manrope text-sm text-[#C9A84C] font-semibold">
                                {stats.percentage.toFixed(1)}% funded
                            </span>
                            <span data-testid="donor-count" className="font-manrope text-sm text-[#F5EDD6]/50">
                                {stats.donor_count} {stats.donor_count === 1 ? "donor" : "donors"}
                            </span>
                        </div>
                    </div>

                    <BtnPrimary to="/donate" testId="fundraising-donate-btn">Make a Donation</BtnPrimary>
                </div>
            </section>

            {/* ===== EVENTS PREVIEW ===== */}
            {events.length > 0 && (
                <section className="py-20 px-4 bg-[#F5EDD6]" data-testid="events-preview-section">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12">
                            <div>
                                <span className="font-marker text-[#C4522A] text-2xl -rotate-1 inline-block mb-2">What's Coming Up</span>
                                <h2 className="font-anton text-4xl sm:text-5xl text-[#3D2008] uppercase">Upcoming Events</h2>
                            </div>
                            <Link to="/events" className="mt-3 sm:mt-0 -ml-2 sm:ml-0 px-2 py-2 font-manrope text-[#C4522A] font-semibold hover:text-[#3D2008] transition-colors inline-flex items-center gap-1">
                                View All <ArrowRight size={16} />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {events.map((event, i) => (
                                <div
                                    key={event.id || i}
                                    className={`bg-white border-2 border-[#3D2008] p-6 shadow-[6px_6px_0px_0px_rgba(45,90,39,0.25)] hover:shadow-[8px_8px_0px_0px_rgba(45,90,39,0.45)] hover:-translate-y-1 transition-all duration-200 ${i === 1 ? "md:mt-6" : ""}`}
                                    data-testid={`event-card-${i}`}
                                >
                                    <div className="flex items-center gap-2 mb-3">
                                        <Calendar size={14} className="text-[#C4522A]" />
                                        <span className="font-manrope text-xs text-[#5C4A35] font-semibold">
                                            {new Date(event.date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                                        </span>
                                    </div>
                                    <h3 className="font-anton text-lg text-[#3D2008] uppercase mb-3">{event.title}</h3>
                                    <p className="font-manrope text-sm text-[#5C4A35] leading-relaxed mb-4">{event.description}</p>
                                    {event.location && (
                                        <div className="flex items-center gap-1 pt-4 border-t border-[#E6DFC8]">
                                            <MapPin size={12} className="text-[#4A7FA5]" />
                                            <span className="font-manrope text-xs text-[#4A7FA5] font-semibold">{event.location}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ===== PARK VISION — removed per Matt's notes ===== */}

            {/* ===== GET INVOLVED CTA ===== */}
            <LatestPosts />

            <section className="py-24 px-4 bg-[#C4522A]" data-testid="get-involved-cta-section">
                <div className="max-w-4xl mx-auto text-center">
                    <span className="font-marker text-white/80 text-2xl -rotate-1 inline-block mb-4">Join the Movement</span>
                    <h2 className="font-anton text-5xl sm:text-7xl text-white uppercase leading-none mb-6">
                        This Park Won't<br />Build Itself
                    </h2>
                    <p className="font-manrope text-white/80 text-xl mb-10 max-w-xl mx-auto leading-relaxed">
                        Donate, volunteer, or spread the word — every bit of support brings us closer to breaking ground.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/get-involved" data-testid="home-getinvolved-btn"
                            className="px-8 py-4 bg-white text-[#C4522A] font-anton uppercase tracking-wider text-lg border-2 border-[#3D2008] shadow-[4px_4px_0px_0px_#3D2008] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150">
                            Get Involved
                        </Link>
                        <Link to="/donate" data-testid="home-donate-btn-2"
                            className="px-8 py-4 bg-transparent text-white font-anton uppercase tracking-wider text-lg border-2 border-white hover:bg-white hover:text-[#C4522A] transition-colors duration-200">
                            Donate
                        </Link>
                    </div>
                </div>
            </section>

            {/* ===== MERCH CTA ===== */}
            {storeOn && (
            <section className="py-16 px-4 bg-[#3D2008]" data-testid="merch-cta-section">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-8 justify-between">
                    <div>
                        <span className="font-marker text-[#C9A84C] text-xl -rotate-1 inline-block mb-3">Rep the Park</span>
                        <h2 className="font-anton text-4xl sm:text-5xl text-[#F5EDD6] uppercase leading-none mb-2">
                            You Can Help —<br />
                            <span className="text-[#C4522A]">Buy Some Merch</span>
                        </h2>
                        <p className="font-manrope text-[#F5EDD6]/65 text-base max-w-sm">
                            Hats, tees, hoodies, stickers, mugs — all printed on demand. Every purchase funds the build.
                        </p>
                    </div>
                    <Link to="/merch" data-testid="merch-shop-now-btn"
                        className="flex-shrink-0 inline-flex items-center gap-2 px-8 py-4 bg-[#C4522A] text-white font-anton uppercase tracking-wider text-lg border-2 border-[#C9A84C] shadow-[4px_4px_0px_0px_rgba(201,168,76,0.5)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150">
                        Shop Now
                    </Link>
                </div>
            </section>
            )}

            {/* ===== SOCIAL MEDIA ===== */}
            <section className="py-16 px-4 bg-[#F5EDD6]">
                <div className="max-w-4xl mx-auto text-center">
                    <span className="font-marker text-[#C4522A] text-2xl -rotate-1 inline-block mb-4">Follow the Build</span>
                    <h2 className="font-anton text-4xl text-[#3D2008] uppercase mb-4">Stay Connected</h2>
                    <p className="font-manrope text-[#5C4A35] text-lg mb-8 max-w-md mx-auto">
                        Follow us for updates, events, and behind-the-scenes looks at our progress.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        {[
                            { label: "Instagram", href: "https://www.instagram.com/seeleylakeskatepark/", color: "bg-[#3D2008]", testId: "home-instagram-link" },
                            { label: "Facebook", href: "https://www.facebook.com/people/Seeley-Lake-Skatepark-Association/61589983814332/", color: "bg-[#4A7FA5]", testId: "home-facebook-link" },
                        ].map(s => (
                            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                                data-testid={s.testId}
                                className={`${s.color} text-white font-manrope font-semibold px-8 py-3 border-2 border-[#3D2008] shadow-[3px_3px_0px_0px_rgba(61,32,8,0.4)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform duration-150`}>
                                {s.label}
                            </a>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
